import React, { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { useDropzone } from "react-dropzone"
import { useMutation, useQuery, useQueryClient } from "react-query"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import VideoFileIcon from "@mui/icons-material/VideoFile"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import LoginIcon from "@mui/icons-material/Login"
import { useAuth } from "../components/Auth"
import ReferencePicker from "../components/clips/ReferencePicker"
import { loginUrl } from "../utils/authApi"
import {
  deleteClip,
  listMyClips,
  listScenes,
  patchClip,
  uploadClip,
} from "../utils/clipsApi"

const VIEWER_BASE = "https://viewer.occluded.app"

const IMAGE_EXT_RE = /\.(jpe?g|png|bmp|tiff?|webp|heic|heif)$/i

/** Image by MIME type or, when the browser reports none (common for dropped
 *  or AirDropped files), by extension. */
export function isImageFile(file) {
  if (!file) return false
  if (file.type && file.type.startsWith("image/")) return true
  return IMAGE_EXT_RE.test(file.name || "")
}

const PREFIX_KEY = "clips.titlePrefix"
const SCENE_KEY = "clips.scene"

/** Default clip title: "<prefix> <frame number>" for a numbered photo
 *  ("off the sickness" + frame_000500.jpg -> "off the sickness 500"),
 *  otherwise "<prefix> <filename stem>"; "-raw" appended for raw-pose clips. */
export function defaultTitle(prefix, file, raw = false) {
  if (!file) return ""
  const stem = (file.name || "").replace(/\.[^.]+$/, "")
  const n = isImageFile(file) ? frameNumberFromName(file.name) : null
  const base = `${(prefix || "").trim()} ${n != null ? n : stem}`.trim()
  return raw ? `${base}-raw` : base
}

/** Raw-pose clips are always titled "...-raw" so they are recognisable. */
export function withRawSuffix(title, raw) {
  const t = (title || "").trim()
  if (!raw) return t
  return /-raw$/i.test(t) ? t : `${t}-raw`
}

/** Trailing number in a filename, sans extension: "frame_000500.jpg" -> 500.
 *  A duplicate-download suffix like "frame_000500 (1).jpg" is ignored. */
export function frameNumberFromName(name) {
  const stem = (name || "")
    .replace(/\.[^.]+$/, "")
    .replace(/\s*\(\d+\)$/, "")
  const m = stem.match(/(\d+)\D*$/)
  return m ? parseInt(m[1], 10) : null
}

const STATUS_CHIP = {
  waiting: { label: "queued", color: "default" },
  processing: { label: "processing", color: "info" },
  done: { label: "ready", color: "success" },
  failed: { label: "failed", color: "error" },
}

const ERROR_HELP = {
  board_not_recognized:
    "We couldn't match this video to the selected board — check the scene, " +
    "lighting, and that the wall is actually in frame.",
  no_climber_detected: "No climber was detected on the wall in this video.",
  reference_not_localized:
    "The reference frame you picked couldn't be matched to the board — pick a " +
    "frame where the wall is clearly in view.",
  no_climber_in_reference: "No climber was detected in the reference frame you picked.",
  bad_video: "The video file couldn't be read.",
  interrupted: "Processing was interrupted — please upload again.",
}

export function UploadCard({ scenes }) {
  const queryClient = useQueryClient()
  const [file, setFile] = useState(null)
  const [scene, setScene] = useState(() => {
    try { return localStorage.getItem(SCENE_KEY) || "" } catch (e) { return "" }
  })
  // null = not edited: the title is derived from prefix/file/raw each render.
  const [titleEdit, setTitleEdit] = useState(null)
  const [rawPose, setRawPose] = useState(false)
  const [prefix, setPrefix] = useState(() => {
    try { return localStorage.getItem(PREFIX_KEY) || "" } catch (e) { return "" }
  })
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)
  // Reference frame + contact taps (see ReferencePicker); both optional.
  const [reference, setReference] = useState({ reference_time_s: null, taps: [] })

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) {
      setFile(accepted[0])
      setTitleEdit(null) // a new file gets a fresh default title
      setReference({ reference_time_s: null, taps: [] })
    }
  }, [])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "video/*": [".mp4", ".mov", ".mkv", ".avi", ".webm", ".m4v"],
      // A single photo becomes a one-frame clip (handy for checking alignment).
      "image/*": [".jpg", ".jpeg", ".png", ".bmp", ".tif", ".tiff", ".webp"],
    },
  })

  const uploading = progress !== null
  const canSubmit = file && scene && !uploading

  // Title: prefix + frame number from the filename, "-raw" when raw pose is
  // ticked (see defaultTitle), unless the user has typed their own. The raw
  // suffix is always enforced on what is sent (see submit).
  const title = titleEdit != null ? titleEdit : defaultTitle(prefix, file, rawPose)
  const finalTitle = withRawSuffix(title, rawPose)

  // The prefix is remembered across uploads (localStorage).
  useEffect(() => {
    try { localStorage.setItem(PREFIX_KEY, prefix) } catch (e) {}
  }, [prefix])
  // Default board: the remembered one if it still exists, else the first.
  useEffect(() => {
    if (!scenes || scenes.length === 0) return
    if (!scenes.some((s) => s.slug === scene)) setScene(scenes[0].slug)
  }, [scenes, scene])
  useEffect(() => {
    if (scene) {
      try { localStorage.setItem(SCENE_KEY, scene) } catch (e) {}
    }
  }, [scene])
  const submit = async () => {
    setError(null)
    setProgress(0)
    try {
      await uploadClip(
        file,
        {
          scene,
          title: finalTitle,
          align_mode: rawPose ? "raw" : "surface",
          reference_time_s: reference.reference_time_s,
          taps: reference.taps.length ? JSON.stringify(reference.taps) : undefined,
        },
        setProgress
      )
      setFile(null)
      setTitleEdit(null)
      queryClient.invalidateQueries("myClips")
    } catch (e) {
      setError(e.message)
    } finally {
      setProgress(null)
    }
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Upload a climb
        </Typography>
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "divider",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            mb: 2,
            bgcolor: isDragActive ? "action.hover" : "transparent",
          }}
        >
          <input {...getInputProps()} />
          <VideoFileIcon sx={{ fontSize: 36, color: "text.secondary" }} />
          <Typography color="text.secondary">
            {file
              ? `${file.name} (${(file.size / 1e6).toFixed(0)} MB)`
              : "Drop a video or photo here, or tap to choose"}
          </Typography>
        </Box>

        <Stack spacing={2}>
          {file && (
            <ReferencePicker file={file} onChange={setReference} disabled={uploading} />
          )}
          <TextField
            select
            label="Board / wall"
            size="small"
            value={scene}
            onChange={(e) => setScene(e.target.value)}
          >
            {(scenes || []).map((s) => (
              <MenuItem key={s.slug} value={s.slug}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Title prefix"
              size="small"
              fullWidth
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              helperText="Remembered. Photo → prefix + frame number from the filename"
            />
            <TextField
              label="Title"
              size="small"
              fullWidth
              value={title}
              onChange={(e) => setTitleEdit(e.target.value)}
              helperText={
                finalTitle !== title ? `Saved as "${finalTitle}"` : undefined
              }
            />
            {/* Problem / grade / angle fields hidden until they are used. */}
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={rawPose}
                onChange={(e) => setRawPose(e.target.checked)}
              />
            }
            label="Debug: raw SAM pose (skip wall snap)"
          />
          {uploading && (
            <LinearProgress
              variant="determinate"
              value={Math.round(progress * 100)}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="contained" disabled={!canSubmit} onClick={submit}>
            {uploading
              ? `Uploading ${Math.round(progress * 100)}%`
              : "Upload & reconstruct"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}

/** Clip title with click-to-rename. Saves on Enter or blur, Esc cancels.
 *  Saved exactly as typed (trimmed): the "-raw" convention only applies to
 *  the default title at upload time, not to a deliberate rename. */
export function ClipTitle({ clip, onRename, saving }) {
  const [draft, setDraft] = useState(null) // null = not editing
  const shown = clip.title || clip.problem_name || "Untitled climb"

  const commit = () => {
    if (draft == null) return
    const next = draft.trim()
    setDraft(null)
    if (next !== (clip.title || "")) onRename(next)
  }

  if (draft != null) {
    return (
      <TextField
        autoFocus
        size="small"
        fullWidth
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit()
          else if (e.key === "Escape") setDraft(null)
        }}
        inputProps={{ "aria-label": "Clip title" }}
      />
    )
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography sx={{ fontWeight: 600 }}>{shown}</Typography>
      <Tooltip title="Rename clip">
        <IconButton
          size="small"
          disabled={saving}
          onClick={() => setDraft(clip.title || "")}
          aria-label="Rename clip"
        >
          <EditOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

function ClipRow({ clip }) {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries("myClips")
  const patch = useMutation((fields) => patchClip(clip.id, fields), {
    onSettled: invalidate,
  })
  const remove = useMutation(() => deleteClip(clip.id), {
    onSettled: invalidate,
  })

  const chip = STATUS_CHIP[clip.status] || STATUS_CHIP.waiting
  const quality = clip.quality
  const created = (clip.created_at || "").slice(0, 16)

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Box sx={{ flexGrow: 1, minWidth: 160 }}>
            <ClipTitle
              clip={clip}
              saving={patch.isLoading}
              onRename={(title) => patch.mutate({ title })}
            />
            <Typography variant="body2" color="text.secondary">
              {[clip.problem_name, clip.grade,
                clip.angle != null ? `${clip.angle}°` : null, created]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>

          <Chip size="small" label={chip.label} color={chip.color} />
          {clip.align_mode === "raw" && (
            <Chip size="small" label="raw pose" variant="outlined" />
          )}
          {clip.align_mode === "plane" && (
            <Chip size="small" label="plane" variant="outlined" />
          )}
          {clip.reference_time_s != null && (
            <Tooltip title={`Reference frame at ${clip.reference_time_s}s, ${(clip.taps || []).length} contact taps`}>
              <Chip size="small" label={`ref ${clip.reference_time_s}s · ${(clip.taps || []).length} taps`} variant="outlined" />
            </Tooltip>
          )}
          {clip.debug_url && (
            <Tooltip title={`Open pod job ${clip.pod_job_id} in JupyterLab`}>
              <Chip
                size="small"
                label="debug"
                variant="outlined"
                clickable
                component="a"
                href={clip.debug_url}
                target="_blank"
                rel="noopener"
              />
            </Tooltip>
          )}
          {clip.status === "processing" && <CircularProgress size={16} />}
          {quality && (
            <Typography variant="body2" color="text.secondary">
              {quality.num_aligned}/{quality.num_frames} frames
              {quality.contact_residual_m != null &&
                ` · contacts within ${Math.round(quality.contact_residual_m * 100)} cm`}
            </Typography>
          )}

          {clip.status === "done" && (
            <>
              <TextField
                select
                size="small"
                value={clip.visibility}
                onChange={(e) => patch.mutate({ visibility: e.target.value })}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="unlisted">Unlisted</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </TextField>
              <Button
                size="small"
                endIcon={<OpenInNewIcon />}
                href={`${VIEWER_BASE}/clip/${clip.id}`}
                target="_blank"
              >
                View in 3D
              </Button>
            </>
          )}

          {clip.status !== "processing" && (
            <Tooltip title="Delete clip">
              <IconButton size="small" onClick={() => remove.mutate()}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {clip.status === "failed" && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {ERROR_HELP[clip.error_code] ||
              `Processing failed (${clip.error_code || "unknown error"}).`}
          </Alert>
        )}
        {clip.status === "done" && quality?.reference_warnings?.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {quality.reference_warnings.join(" ")}
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default function Clips() {
  const { user, loading } = useAuth()

  const isAdmin = !!user?.is_admin
  const { data: scenesData } = useQuery("scenes", listScenes, {
    enabled: isAdmin,
  })
  const { data: clipsData } = useQuery("myClips", listMyClips, {
    enabled: isAdmin,
    // keep polling while anything is queued or on the GPU
    refetchInterval: (data) =>
      data?.clips?.some((c) => c.status === "waiting" || c.status === "processing")
        ? 10000
        : false,
  })

  if (loading) return null
  if (!user) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", textAlign: "center", mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          Your climbs in 3D
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Upload a video of yourself on a known board and get back a 3D
          reconstruction of the attempt. Sign in to get started.
        </Typography>
        <Button variant="contained" startIcon={<LoginIcon />} href={loginUrl()}>
          Sign in with Google
        </Button>
      </Box>
    )
  }
  // Unreleased feature: admin-only for now (the API enforces this too).
  if (!isAdmin) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", textAlign: "center", mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          Coming soon
        </Typography>
        <Typography color="text.secondary">
          3D clips aren&apos;t available yet — check back soon.
        </Typography>
      </Box>
    )
  }

  const clips = clipsData?.clips || []
  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        My clips
      </Typography>
      <UploadCard scenes={scenesData?.scenes} />
      {clips.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
          No clips yet — upload your first climb above.
        </Typography>
      ) : (
        clips.map((clip) => <ClipRow key={clip.id} clip={clip} />)
      )}
    </Box>
  )
}
